import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { capabilityLevelKeys, roleLevelKeys } from "@/lib/query";
import type { LevelNameInput, LevelVisibilityInput } from "@/schemas/capability-levels";

/** One rung of the global scale as managed on Settings → Performance ratings. */
export interface CapabilityScaleRow {
  id: string;
  ordinal: number;
  name: string;
  description: string | null;
  /** Offered when rating someone on their Person Profile. */
  showInApp: boolean;
  /** Offered on the feedback survey. */
  showOnFeedback: boolean;
}

/** The global rating scale itself (Settings → Config → Performance ratings). */
export function useCapabilityScale() {
  return useQuery({
    queryKey: capabilityLevelKeys.scale,
    queryFn: async (): Promise<CapabilityScaleRow[]> => {
      const res = await fetch("/api/capability-levels");
      if (!res.ok) throw new Error("Failed to fetch performance ratings");
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * Role expectation grids embed the rating names as their column headers but
 * cache under their own key root, so a rename has to reach across to them —
 * `capabilityLevelKeys.all` alone leaves every open grid captioned with the old
 * name until a reload.
 */
function invalidateScaleConsumers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: capabilityLevelKeys.all });
  qc.invalidateQueries({ queryKey: roleLevelKeys.all });
}

/** Rename one rung of the global scale, or edit its definition. Admin-only server-side. */
export function useRenameCapabilityLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LevelNameInput) => {
      const res = await fetch("/api/capability-levels", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateScaleConsumers(qc);
      toast.success("Performance rating updated");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to update the rating"),
  });
}

/**
 * Toggle where one rung is offered (Person Profile / feedback survey).
 *
 * The row is updated optimistically so the checkbox responds on click rather
 * than after the round trip — a checkbox that lags reads as an ignored click
 * and invites a second one. The snapshot is restored if the write fails.
 */
export function useSetCapabilityLevelVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LevelVisibilityInput) => {
      const res = await fetch("/api/capability-levels/visibility", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
      return res.json();
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: capabilityLevelKeys.scale });
      const previous = qc.getQueryData<CapabilityScaleRow[]>(capabilityLevelKeys.scale);
      qc.setQueryData<CapabilityScaleRow[]>(capabilityLevelKeys.scale, (rows) =>
        rows?.map((r) =>
          r.ordinal === input.ordinal
            ? {
                ...r,
                showInApp: input.showInApp ?? r.showInApp,
                showOnFeedback: input.showOnFeedback ?? r.showOnFeedback,
              }
            : r,
        ),
      );
      return { previous };
    },
    onError: (e: Error, _input, context) => {
      if (context?.previous) qc.setQueryData(capabilityLevelKeys.scale, context.previous);
      toast.error(e.message || "Failed to update where this rating is offered");
    },
    onSettled: () => {
      // Hiding a rating removes its column from every role's expectation grid,
      // which caches under its own key root — so this has to reach across too,
      // or the grid keeps rendering a rating that's just been switched off.
      invalidateScaleConsumers(qc);
    },
  });
}
